import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { CgClose } from 'react-icons/cg';
import { MdDelete } from 'react-icons/md';
import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import SpiritoryInput from '@/components/common/Forms/SpiritoryInput';
import SpiritoryTextarea from '@/components/common/Forms/SpiritoryTextarea';
import SpiritorySelect from '@/components/common/Forms/SpiritorySelect';
import { FileUploader } from 'react-drag-drop-files';
import { toast } from 'react-toastify';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { StKilianEnum } from '@/types/translations/enums';
import doSubmitStKilianSalesForm from '@/api/doSubmitStKilianSalesForm';

interface SalesFormModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  name: string;
  email: string;
  photos: File[];
  bottlingYear: string;
  alcoholContent: string;
  bottleSize: string;
  numberOfBottles: string;
  caskType: string;
  caskNumber: string;
  bottlingName: string;
  additionalDetails: string;
}

// Bottle size options will be created inside the component to use translations

const fileTypes = ['JPG', 'PNG', 'JPEG', 'WEBP'];

export default function SalesFormModal({ isOpen, onClose }: SalesFormModalProps) {
  const { translate } = useTypedTranslation<StKilianEnum>('st_kilian');

  // Create bottle size options with translations
  const bottleSizeOptions = [
    { value: '0.5', label: '0,5 Liter' },
    { value: '0.7', label: '0,7 Liter' },
    { value: '0.75', label: '0,75 Liter' },
    { value: '1.0', label: '1,0 Liter' },
    { value: '1.5', label: '1,5 Liter' },
  ];

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    photos: [],
    bottlingYear: '',
    alcoholContent: '',
    bottleSize: '',
    numberOfBottles: '',
    caskType: '',
    caskNumber: '',
    bottlingName: '',
    additionalDetails: '',
  });

  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const maxFileSize = 5 * 1024 * 1024; // 5MB in bytes
    const maxFiles = 10;

    // Check total number of files (including existing ones)
    if (formData.photos.length + fileArray.length > maxFiles) {
      toast.error(
        `Maximum ${maxFiles} photos allowed. You currently have ${formData.photos.length} photos.`
      );
      return;
    }

    // Validate each file size
    const oversizedFiles: string[] = [];
    const validFiles: File[] = [];

    fileArray.forEach(file => {
      if (file.size > maxFileSize) {
        oversizedFiles.push(`${file.name} (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      } else {
        validFiles.push(file);
      }
    });

    // Show error for oversized files
    if (oversizedFiles.length > 0) {
      toast.error(`Files too large (max 5MB): ${oversizedFiles.join(', ')}`);
    }

    // Add only valid files
    if (validFiles.length > 0) {
      setFormData(prev => ({ ...prev, photos: [...prev.photos, ...validFiles] }));
    }
  };

  const removeFile = (indexToRemove: number) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, index) => index !== indexToRemove),
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = translate(StKilianEnum['sales_form.validation.name_required']);
    }

    if (!formData.email.trim()) {
      newErrors.email = translate(StKilianEnum['sales_form.validation.email_required']);
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = translate(StKilianEnum['sales_form.validation.email_invalid']);
    }

    if (formData.photos.length === 0) {
      newErrors.photos = translate(StKilianEnum['sales_form.validation.photos_required']);
    }

    if (!formData.bottlingYear.trim()) {
      newErrors.bottlingYear = translate(
        StKilianEnum['sales_form.validation.bottling_year_required']
      );
    } else {
      const year = parseInt(formData.bottlingYear);
      if (isNaN(year) || year < 1900 || year > new Date().getFullYear() + 10) {
        newErrors.bottlingYear =
          'Please enter a valid year between 1900 and ' + (new Date().getFullYear() + 10);
      }
    }

    if (!formData.alcoholContent.trim()) {
      newErrors.alcoholContent = translate(
        StKilianEnum['sales_form.validation.alcohol_content_required']
      );
    } else {
      const alcohol = parseFloat(formData.alcoholContent);
      if (isNaN(alcohol) || alcohol < 0 || alcohol > 100) {
        newErrors.alcoholContent = 'Please enter a valid alcohol content between 0 and 100';
      }
    }

    if (!formData.bottleSize) {
      newErrors.bottleSize = translate(StKilianEnum['sales_form.validation.bottle_size_required']);
    }

    if (!formData.numberOfBottles.trim()) {
      newErrors.numberOfBottles = translate(
        StKilianEnum['sales_form.validation.number_of_bottles_required']
      );
    } else {
      const bottles = parseInt(formData.numberOfBottles);
      if (isNaN(bottles) || bottles < 1 || bottles > 10000) {
        newErrors.numberOfBottles = 'Please enter a valid number of bottles between 1 and 10000';
      }
    }

    if (!formData.caskType.trim()) {
      newErrors.caskType = translate(StKilianEnum['sales_form.validation.cask_type_required']);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare data for API call
      const submitData = {
        name: formData.name,
        email: formData.email,
        photos: formData.photos,
        bottlingYear: parseInt(formData.bottlingYear),
        alcoholContent: parseFloat(formData.alcoholContent),
        bottleSize: formData.bottleSize,
        numberOfBottles: parseInt(formData.numberOfBottles),
        caskType: formData.caskType,
        caskNumber: formData.caskNumber || undefined,
        bottlingName: formData.bottlingName || undefined,
        additionalDetails: formData.additionalDetails || undefined,
      };

      // Submit to backend
      await doSubmitStKilianSalesForm(submitData);

      // Show thank you page instead of closing modal
      setShowThankYou(true);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error(translate(StKilianEnum['sales_form.messages.error']));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToPage = () => {
    // Reset form and close modal
    setFormData({
      name: '',
      email: '',
      photos: [],
      bottlingYear: '',
      alcoholContent: '',
      bottleSize: '',
      numberOfBottles: '',
      caskType: '',
      caskNumber: '',
      bottlingName: '',
      additionalDetails: '',
    });
    setShowThankYou(false);
    setErrors({});
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="tw-relative tw-z-[99] max-nine:tw-p-2" onClose={onClose}>
        <div className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-60" />
        <div className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-overflow-y-auto tw-p-4">
          <TransitionChild
            as={Fragment}
            enter="tw-ease-out tw-duration-300"
            enterFrom="tw-opacity-0 tw-scale-95"
            enterTo="tw-opacity-100 tw-scale-100"
            leave="tw-ease-in tw-duration-200"
            leaveFrom="tw-opacity-100 tw-scale-100"
            leaveTo="tw-opacity-0 tw-scale-95"
          >
            <DialogPanel className="tw-relative tw-my-8 tw-w-full tw-max-w-6xl tw-overflow-hidden tw-bg-white tw-shadow-lg max-md:tw-mx-4 max-md:tw-my-4">
              {/* Close Button - Top Right */}
              <button
                onClick={onClose}
                className="tw-absolute tw-right-4 tw-top-4 tw-z-10 tw-border-none tw-bg-transparent tw-text-white tw-transition-colors hover:tw-text-gray-200"
              >
                <CgClose className="tw-text-2xl" />
              </button>

              <div className="tw-flex tw-max-h-[90vh] tw-min-h-[600px] max-md:tw-max-h-[95vh] max-md:tw-min-h-[500px] max-md:tw-flex-col">
                {!showThankYou ? (
                  <>
                    {/* Left Column - Form */}
                    <div className="tw-flex tw-w-1/2 tw-flex-col tw-overflow-y-auto tw-p-8 max-md:tw-w-full max-md:tw-p-4">
                      <div className="max-lg:tw-mb-6 max-lg:tw-bg-stKilianBgModal max-lg:tw-bg-cover max-lg:tw-bg-[70%] max-lg:tw-p-4 max-lg:tw-text-white">
                        <div className="max-lg:tw-mt-11 lg:tw-mb-8">
                          <DialogTitle className="tw-text-3xl tw-font-bold tw-uppercase max-lg:tw-text-xl">
                            {translate(StKilianEnum['sales_form.title'])}
                          </DialogTitle>
                        </div>

                        <p className="max-lg:tw-text-sm lg:tw-mb-8">
                          {translate(StKilianEnum['sales_form.description'])}
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="tw-space-y-6">
                        {/* Name and Email Row */}
                        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.name'])}
                            type="text"
                            placeholder={translate(StKilianEnum['sales_form.fields.name'])}
                            value={formData.name}
                            onChange={e => handleInputChange('name', e.target.value)}
                            required
                            error={errors.name}
                          />
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.email'])}
                            type="email"
                            placeholder={translate(
                              StKilianEnum['sales_form.fields.email_placeholder']
                            )}
                            value={formData.email}
                            onChange={e => handleInputChange('email', e.target.value)}
                            required
                            error={errors.email}
                          />
                        </div>

                        {/* File Upload */}
                        <div className="tw-space-y-3">
                          <label
                            htmlFor="photos"
                            className="tw-text-base tw-font-semibold tw-leading-[20.16px] tw-text-slate-gray"
                          >
                            {translate(StKilianEnum['sales_form.fields.photos'])}{' '}
                            <span className="tw-text-[#C51F3E]">*</span>
                          </label>
                          <p className="tw-mb-2 tw-text-xs tw-text-gray-500">
                            Maximum 10 photos, 5MB per photo
                          </p>

                          {/* Selected Files Display */}
                          {formData.photos.length > 0 && (
                            <div className="tw-space-y-3">
                              <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                                {formData.photos.map((file, index) => (
                                  <div
                                    key={index}
                                    className="tw-relative tw-flex tw-items-center tw-gap-2 tw-rounded-md tw-border tw-border-gray-200 tw-bg-gray-50 tw-px-2 tw-py-1.5"
                                  >
                                    <div className="tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-rounded tw-bg-gray-200">
                                      <svg
                                        width="12"
                                        height="12"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="tw-text-gray-500"
                                      >
                                        <path
                                          d="M21 19V5C21 3.9 20.1 3 19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19ZM8.5 13.5L11 16.51L14.5 12L19 18H5L8.5 13.5Z"
                                          fill="currentColor"
                                        />
                                      </svg>
                                    </div>
                                    <div className="tw-min-w-0 tw-flex-1">
                                      <p className="tw-truncate tw-text-xs tw-font-medium tw-text-gray-900">
                                        {file.name}
                                      </p>
                                      <p className="tw-text-xs tw-text-gray-500">
                                        {(file.size / 1024 / 1024).toFixed(1)} MB
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => removeFile(index)}
                                      className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-bg-red-100 tw-text-red-600 tw-transition-colors hover:tw-bg-red-200"
                                    >
                                      <MdDelete className="tw-text-md" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Upload Area */}
                          <FileUploader
                            multiple={true}
                            handleChange={handleFileChange}
                            name="photos"
                            types={fileTypes}
                            classes="tw-w-full"
                          >
                            <div className="tw-flex tw-cursor-pointer tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border-2 tw-border-dashed tw-border-gray-300 tw-bg-gray-50 tw-px-6 tw-py-8 tw-transition-colors hover:tw-bg-gray-100">
                              <div className="tw-mb-3 tw-text-3xl tw-text-gray-400">
                                <svg
                                  width="40"
                                  height="40"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="tw-mx-auto"
                                >
                                  <path
                                    d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.89 22 5.99 22H18C19.1 22 20 21.1 20 20V8L14 2ZM18 20H6V4H13V9H18V20Z"
                                    fill="currentColor"
                                  />
                                </svg>
                              </div>
                              <p className="tw-mb-3 tw-text-center tw-text-sm">
                                {formData.photos.length > 0
                                  ? translate(StKilianEnum['sales_form.file_upload.add_more_files'])
                                  : translate(
                                      StKilianEnum['sales_form.file_upload.drag_drop_text']
                                    )}
                              </p>
                              <button
                                type="button"
                                className="tw-rounded tw-border tw-border-gray-300 tw-bg-gray-200 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-transition-colors hover:tw-bg-gray-300"
                              >
                                {formData.photos.length > 0
                                  ? translate(
                                      StKilianEnum['sales_form.file_upload.add_more_files_button']
                                    )
                                  : translate(StKilianEnum['sales_form.file_upload.select_files'])}
                              </button>
                            </div>
                          </FileUploader>

                          {errors.photos && (
                            <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-red-500">
                              <span>⚠</span> {errors.photos}
                            </div>
                          )}
                        </div>

                        {/* Bottling Year and Alcohol Content Row */}
                        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.bottling_year'])}
                            type="number"
                            placeholder={translate(
                              StKilianEnum['sales_form.fields.bottling_year_placeholder']
                            )}
                            value={formData.bottlingYear}
                            onChange={e => handleInputChange('bottlingYear', e.target.value)}
                            required
                            error={errors.bottlingYear}
                            min="1900"
                            max={new Date().getFullYear() + 10}
                          />
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.alcohol_content'])}
                            type="number"
                            step="0.1"
                            placeholder={translate(
                              StKilianEnum['sales_form.fields.alcohol_content_placeholder']
                            )}
                            value={formData.alcoholContent}
                            onChange={e => handleInputChange('alcoholContent', e.target.value)}
                            required
                            error={errors.alcoholContent}
                            min="0"
                            max="100"
                          />
                        </div>

                        {/* Bottle Size and Number of Bottles Row */}
                        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                          <div>
                            <SpiritorySelect
                              label={translate(StKilianEnum['sales_form.fields.bottle_size'])}
                              name="bottleSize"
                              value={formData.bottleSize}
                              onChange={e => handleInputChange('bottleSize', e.target.value)}
                              options={bottleSizeOptions}
                              defaultOptionText={translate(
                                StKilianEnum['sales_form.fields.bottle_size_placeholder']
                              )}
                              required
                            />
                            {errors.bottleSize && (
                              <div className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-red-500">
                                <span>⚠</span> {errors.bottleSize}
                              </div>
                            )}
                          </div>
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.number_of_bottles'])}
                            type="number"
                            placeholder={translate(
                              StKilianEnum['sales_form.fields.number_of_bottles_placeholder']
                            )}
                            value={formData.numberOfBottles}
                            onChange={e => handleInputChange('numberOfBottles', e.target.value)}
                            required
                            error={errors.numberOfBottles}
                            min="1"
                            max="10000"
                          />
                        </div>

                        {/* Cask Type and Cask Number Row */}
                        <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.cask_type'])}
                            type="text"
                            placeholder={translate(
                              StKilianEnum['sales_form.fields.cask_type_placeholder']
                            )}
                            value={formData.caskType}
                            onChange={e => handleInputChange('caskType', e.target.value)}
                            required
                            error={errors.caskType}
                          />
                          <SpiritoryInput
                            label={translate(StKilianEnum['sales_form.fields.cask_number'])}
                            type="text"
                            placeholder={translate(
                              StKilianEnum['sales_form.fields.cask_number_placeholder']
                            )}
                            value={formData.caskNumber}
                            onChange={e => handleInputChange('caskNumber', e.target.value)}
                          />
                        </div>

                        {/* Bottling Name */}
                        <SpiritoryInput
                          label={translate(StKilianEnum['sales_form.fields.bottling_name'])}
                          type="text"
                          placeholder={translate(
                            StKilianEnum['sales_form.fields.bottling_name_placeholder']
                          )}
                          value={formData.bottlingName}
                          onChange={e => handleInputChange('bottlingName', e.target.value)}
                        />

                        {/* Additional Details */}
                        <SpiritoryTextarea
                          label={translate(StKilianEnum['sales_form.fields.additional_details'])}
                          placeholder={translate(
                            StKilianEnum['sales_form.fields.additional_details_placeholder']
                          )}
                          value={formData.additionalDetails}
                          onChange={e => handleInputChange('additionalDetails', e.target.value)}
                          rows={4}
                        />

                        {/* Submit Button */}
                        <div className="tw-flex tw-justify-center tw-pt-6">
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className={clsx(
                              'tw-w-full tw-max-w-md tw-bg-gray-800 tw-px-8 tw-py-4 tw-font-bold tw-uppercase tw-text-white tw-transition-colors',
                              {
                                'hover:tw-bg-gray-900': !isSubmitting,
                                'tw-cursor-not-allowed tw-opacity-50': isSubmitting,
                              }
                            )}
                          >
                            {isSubmitting
                              ? translate(StKilianEnum['sales_form.submit.sending'])
                              : translate(StKilianEnum['sales_form.submit.send_request'])}
                          </button>
                        </div>
                      </form>
                    </div>

                    {/* Right Column - Image */}
                    <div className="tw-flex tw-w-1/2 tw-bg-stKilianBgModal tw-bg-cover tw-bg-[70%] max-lg:tw-hidden max-md:tw-min-h-[300px] max-md:tw-w-full"></div>
                  </>
                ) : (
                  <>
                    {/* Thank You Page */}
                    <div className="tw-flex tw-h-full tw-w-1/2 tw-flex-col tw-justify-center tw-p-8 max-md:tw-w-full max-md:tw-p-4">
                      <DialogTitle className="tw-mb-8 tw-text-3xl tw-font-bold tw-uppercase tw-text-gray-800 max-lg:tw-text-xl">
                        {translate(StKilianEnum['sales_form.title'])}
                      </DialogTitle>

                      <div className="tw-flex tw-items-center">
                        {/* Checkmark Icon */}
                        <div className="tw-mb-6 tw-flex tw-justify-center">
                          <div className="tw-flex tw-h-16 tw-w-16 tw-items-center tw-justify-center tw-rounded-full tw-border-4 tw-border-orange-500 tw-bg-white">
                            <Check className="tw-h-8 tw-w-8 tw-text-orange-500" />
                          </div>
                        </div>

                        {/* Thank You Message */}
                        <h2 className="tw-mb-6 tw-text-2xl tw-font-bold tw-uppercase tw-text-gray-800 max-lg:tw-text-xl">
                          {translate(StKilianEnum['sales_form.thank_you.title'])}
                        </h2>
                      </div>

                      {/* Description */}
                      <p className="tw-mb-8 tw-text-start tw-text-base tw-text-gray-600 max-lg:tw-text-sm">
                        {translate(StKilianEnum['sales_form.thank_you.description'])}
                      </p>

                      {/* Back Button */}
                      <button
                        onClick={handleBackToPage}
                        className="tw-mt-16 tw-rounded tw-bg-gray-800 tw-px-8 tw-py-3 tw-font-bold tw-uppercase tw-text-white tw-transition-colors hover:tw-bg-gray-900"
                      >
                        {translate(StKilianEnum['sales_form.thank_you.back_button'])}
                      </button>
                    </div>

                    {/* Right Column - Image */}
                    <div className="tw-flex tw-w-1/2 tw-bg-stKilianBgModal tw-bg-cover tw-bg-[70%] max-lg:tw-hidden max-md:tw-min-h-[300px] max-md:tw-w-full"></div>
                  </>
                )}
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
